/**
 * Navigation Guard - Prevents duplicate navigation and multiple clicks
 */
class NavigationGuard {
  constructor() {
    this.isNavigating = false;
    this.navigationTimeout = null;
    this.lastNavigation = null;
    this.lastNavigationTime = null;
    this.blockedElements = new WeakSet();
  }

  /** Execute navigation with protection against multiple calls */
  safeNavigate(callback, options = {}) {
    const { delay = 300, preventDuplicate = true, element = null } = options;

    if (this.isNavigating) return false;
    if (element && this.blockedElements.has(element)) return false;

    const navigationKey = callback.toString();
    if (preventDuplicate && this.lastNavigation === navigationKey) {
      if (Date.now() - this.lastNavigationTime < delay) return false;
    }

    this.isNavigating = true;
    this.lastNavigation = navigationKey;
    this.lastNavigationTime = Date.now();

    if (element) {
      this.blockedElements.add(element);
      element.style.pointerEvents = 'none';
      element.style.opacity = '0.7';
    }

    if (this.navigationTimeout) {
      clearTimeout(this.navigationTimeout);
    }

    try {
      const result = callback();

      this.navigationTimeout = setTimeout(() => {
        this.isNavigating = false;
        if (element) {
          this.blockedElements.delete(element);
          element.style.pointerEvents = '';
          element.style.opacity = '';
        }
      }, delay);

      return result;
    } catch (error) {
      this.isNavigating = false;
      if (element) {
        this.blockedElements.delete(element);
        element.style.pointerEvents = '';
        element.style.opacity = '';
      }
      throw error;
    }
  }

  /** Reset guard state */
  reset() {
    this.isNavigating = false;
    this.lastNavigation = null;
    this.lastNavigationTime = null;
    this.blockedElements = new WeakSet();

    if (this.navigationTimeout) {
      clearTimeout(this.navigationTimeout);
      this.navigationTimeout = null;
    }
  }
}

export const navigationGuard = new NavigationGuard();

window.addEventListener('popstate', () => {
  setTimeout(() => navigationGuard.reset(), 100);
});